/**
 * Product swatches
 *
 * The server renders a curated set of colourways from the product's
 * `product_data.swatches` metafield. That set is correct but manual, so it can
 * drift from what is actually in the catalogue.
 *
 * This replaces it with the live siblings: every product carrying the same
 * `style:<code>` tag, fetched through the Section Rendering API. It is pure
 * progressive enhancement - every failure path leaves the curated swatches
 * exactly where they are, so the row is never worse than what Liquid rendered.
 *
 * Results are cached in sessionStorage per style code. Every colourway of one
 * product returns the same set, so walking a row of swatches costs one request
 * rather than one per PDP.
 */

/**
 * Bumped whenever the swatch markup changes. Cached responses are keyed on it,
 * so a browser holding yesterday's HTML re-fetches instead of replaying markup
 * that predates the change.
 */
const CACHE_VERSION = 2;

const CONTAINER = ".product-swatches";
const SWATCHES = ".swatches";
const SWATCH = ".swatch";
const NAME = ".swatch-name";

/** Hovering a swatch previews its colour name; leaving restores the current one. */
function bindNamePreview(container) {
  const swatches = container.querySelector(SWATCHES);
  const heading = container.querySelector(NAME);
  if (!swatches || !heading) return;

  const currentName = heading.getAttribute("data-swatch-name") || "";

  // Delegated, so swatches swapped in by the fetch below get this too.
  swatches.addEventListener("mouseover", (event) => {
    const swatch = event.target.closest(SWATCH);
    if (swatch) heading.textContent = swatch.getAttribute("data-swatch-name") || "";
  });

  swatches.addEventListener("mouseout", (event) => {
    if (event.target.closest(SWATCH)) heading.textContent = currentName;
  });
}

/**
 * Turns the fetched section HTML into the new swatch row.
 * Returns false whenever the response is unusable, which leaves the curated
 * swatches untouched and stops the result being cached.
 */
function applyRelated(container, html) {
  const swatches = container.querySelector(SWATCHES);
  if (!swatches) return false;

  const template = document.createElement("template");
  template.innerHTML = html.trim();

  const results = template.content.querySelector(".related-swatches-results");
  if (!results) return false;

  const currentHandle = (container.dataset.productHandle || "").toLowerCase();
  const currentUrl =
    swatches.querySelector(`${SWATCH}[aria-current="true"]`)?.getAttribute("href") ||
    `${Theme.routes.root_url.replace(/\/$/, "")}/products/${currentHandle}`;
  const currentName = container.dataset.currentSwatchName || "";
  const currentColour = container.dataset.currentSwatchColour || "";
  const currentImage = container.dataset.currentSwatchImage || "";

  const byHandle = new Map();
  results.querySelectorAll(SWATCH).forEach((anchor) => {
    const handle = (anchor.dataset.handle || "").toLowerCase();
    if (!handle || byHandle.has(handle)) return;

    // Drop the search query string (?_pos&_sid&_ss=r) so the URL is clean.
    // Read the attribute rather than the .pathname property: these anchors
    // are still inside a <template>, whose contents belong to an inert
    // document with no base URL, so .pathname resolves to an empty string.
    const href = anchor.getAttribute("href") || "";
    anchor.setAttribute("href", href.split(/[?#]/)[0]);

    byHandle.set(handle, anchor);
  });

  if (byHandle.size === 0) return false;

  // The search should return the current product, but rebuild it from the
  // data attributes if it does not - a row missing the colour you are looking
  // at reads as a bug.
  if (currentHandle && !byHandle.has(currentHandle) && currentName && currentColour) {
    const self = document.createElement("a");
    self.href = currentUrl;
    self.className = "swatch";
    self.style.background = currentColour;
    self.setAttribute("data-swatch-name", currentName);
    self.setAttribute("aria-label", currentName);
    self.dataset.handle = currentHandle;

    // Carries the image too, so the rebuilt swatch is not the odd empty one
    // in the row when the block is in image mode.
    if (currentImage) {
      const image = document.createElement("img");
      image.src = currentImage;
      image.alt = "";
      image.loading = "lazy";
      self.appendChild(image);
    }

    byHandle.set(currentHandle, self);
  }

  // Alphabetical by colour name, so the row is in the same order on every
  // colourway's PDP rather than reshuffling as you click through them.
  const ordered = Array.from(byHandle.values()).sort((a, b) =>
    (a.getAttribute("data-swatch-name") || "").localeCompare(
      b.getAttribute("data-swatch-name") || "",
    ),
  );

  const fragment = document.createDocumentFragment();
  ordered.forEach((swatch) => {
    const isCurrent = (swatch.dataset.handle || "").toLowerCase() === currentHandle;
    swatch.classList.toggle("selected", isCurrent);
    if (isCurrent) {
      swatch.setAttribute("href", currentUrl);
      swatch.setAttribute("aria-current", "true");
    } else {
      swatch.removeAttribute("aria-current");
    }
    fragment.appendChild(swatch);
  });

  swatches.replaceChildren(fragment);
  container.removeAttribute("hidden");
  return true;
}

function enhance(container) {
  const styleCode = (container.dataset.styleCode || "").trim().toLowerCase();
  if (!styleCode) return; // No style tag - the curated set is all there is.

  const cacheKey = `swatch:v${CACHE_VERSION}:${styleCode}`;

  let cached = null;
  try {
    cached = sessionStorage.getItem(cacheKey);
  } catch (error) {
    // Private browsing can throw on read as well as write.
  }

  if (cached) {
    applyRelated(container, cached);
    return;
  }

  const query = encodeURIComponent(`tag:"style:${styleCode}"`);

  fetch(`${Theme.routes.search_url}?q=${query}&type=product&section_id=api-related-swatches`)
    .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
    .then((html) => {
      if (!applyRelated(container, html)) return;
      try {
        sessionStorage.setItem(cacheKey, html);
      } catch (error) {
        // Over quota - the swatches are already in place, only the cache is lost.
      }
    })
    .catch(() => {
      // Leave the curated metafield swatches exactly as rendered.
    });
}

/**
 * A product grid can hold dozens of cards, and fetching for all of them at
 * load would be a burst of requests for swatches nobody has scrolled to yet.
 * Cards wait until they are near the viewport; the PDP's own row does not,
 * since it is on screen from the start.
 */
let observer = null;

function getObserver() {
  if (observer) return observer;
  if (!("IntersectionObserver" in window)) return null;

  observer = new IntersectionObserver(
    (entries, self) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        self.unobserve(entry.target);
        enhance(entry.target);
      });
    },
    { rootMargin: "200px" },
  );

  return observer;
}

function schedule(container) {
  if (!container.closest(".product-card")) {
    enhance(container);
    return;
  }

  const io = getObserver();
  if (io) io.observe(container);
  else enhance(container); // No IntersectionObserver - fetch now.
}

/**
 * On a card a swatch is a filter, not a link: clicking it turns the card into
 * that colourway rather than navigating away. The replacement markup comes
 * from the api-product-card section, so the images, title, price, gender line
 * and swatch row are all rendered by the real card snippet.
 *
 * The whole <product-card> is swapped rather than its contents, so the custom
 * element is constructed fresh instead of being left with listeners bound to
 * children that no longer exist.
 *
 * The anchor keeps its href throughout: with no JavaScript, or before this
 * resolves, clicking a swatch still goes to the product.
 */
function swapCard(card, swatch) {
  const href = swatch.getAttribute("href");
  if (!href || card.dataset.swatchSwapping === "true") return;
  if (swatch.getAttribute("aria-current") === "true") return;

  card.dataset.swatchSwapping = "true";

  fetch(`${href}?section_id=api-product-card`)
    .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
    .then((html) => {
      const template = document.createElement("template");
      template.innerHTML = html.trim();

      const fresh = template.content.querySelector("product-card");
      if (!fresh) return;

      // The row is the same set of colourways either way, so keep the reader
      // looking at the swatch they just clicked rather than snapping to 0.
      const scrolled = card.querySelector(SWATCHES)?.scrollLeft || 0;

      card.replaceWith(fresh);
      init(fresh);

      const row = fresh.querySelector(SWATCHES);
      if (row) row.scrollLeft = scrolled;
    })
    .catch(() => {
      // Leave the card as it was - the swatch is still a working link.
    })
    .finally(() => {
      delete card.dataset.swatchSwapping;
    });
}

document.addEventListener("click", (event) => {
  const swatch = event.target.closest(`.product-card ${CONTAINER} ${SWATCH}`);
  if (!swatch) return;

  // Let a modified click open the colourway in a new tab, as a link should.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  event.preventDefault();

  const card = swatch.closest("product-card");
  if (card) swapCard(card, swatch);
});

function init(scope) {
  (scope || document).querySelectorAll(CONTAINER).forEach((container) => {
    if (container.dataset.swatchesReady === "true") return;
    container.dataset.swatchesReady = "true";
    bindNamePreview(container);
    schedule(container);
  });
}

init();

document.addEventListener("shopify:section:load", (event) => init(event.target));

/**
 * Filtering, sorting and load-more all replace or append card markup, and the
 * new cards arrive with no listeners. Re-running init is cheap - the
 * data-swatches-ready flag makes it a no-op for containers already handled.
 */
if ("MutationObserver" in window) {
  let pending = false;

  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      init();
    });
  }).observe(document.body, { childList: true, subtree: true });
}
